import React from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { featureFlags } from '../../config/featureFlags'
import { useI18n } from '../../i18n'
import ComplianceWorkflowWorkspace from '../../components/compliance-workflow/ComplianceWorkflowWorkspace'
import { API_ENDPOINTS } from '../../config/api'
import { fetchWorkflowList } from '../../services/complianceWorkflowApi'
import { useAuth } from '../../components/AuthProvider'
import { Button } from '../../components/ui/Button'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import { isWorkflowActiveStage } from '../../contexts/ComplianceWorkflowIndexContext'
import { getStandardDisplayName, normalizeComplianceStandardCode } from '../../utils/complianceStandard'
import { fetchEffectiveStandardAccess } from '../../services/standardAccessApi'
import { getActiveOrgId } from '../../utils/orgUtils'
import { resolveVisibleStandardCodes } from '../../utils/standardAccessPolicy'
import OperationalWorkflowAuditReadinessSummary from '../../components/compliance-workflow/OperationalWorkflowAuditReadinessSummary'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import wfStyles from '@/styles/pages/compliance-workflow/ComplianceWorkflow.module.scss'

export default function ComplianceWorkflowPage() {
  const { standardCode } = useParams<{ standardCode?: string }>()
  const navigate = useNavigate()
  const { t } = useI18n()
  const { token } = useAuth()
  const [workflows, setWorkflows] = React.useState<Array<{ standardCode: string; workflowStage: string }>>([])
  const [loading, setLoading] = React.useState(!standardCode)
  const [enabledStandards, setEnabledStandards] = React.useState<Set<string>>(new Set())
  const [effectiveAccess, setEffectiveAccess] = React.useState<Awaited<
    ReturnType<typeof fetchEffectiveStandardAccess>
  > | null>(null)
  const [accessLoading, setAccessLoading] = React.useState(true)

  React.useEffect(() => {
    if (!token) return
    void fetch(`${API_ENDPOINTS.COMPLIANCE_STANDARDS_ENABLED}?includeAll=0`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json().catch(() => ({})))
      .then((json) => {
        const codes = Array.isArray(json?.data?.enabledStandards) ? json.data.enabledStandards : []
        setEnabledStandards(new Set(codes.map((c: string) => normalizeComplianceStandardCode(c))))
      })
      .catch(() => setEnabledStandards(new Set()))
  }, [token])

  React.useEffect(() => {
    const orgId = getActiveOrgId()
    if (!orgId) {
      setAccessLoading(false)
      return
    }
    setAccessLoading(true)
    void fetchEffectiveStandardAccess(orgId)
      .then(setEffectiveAccess)
      .catch(() => setEffectiveAccess(null))
      .finally(() => setAccessLoading(false))
  }, [])

  React.useEffect(() => {
    if (!featureFlags.COMPLIANCE_WORKFLOW_V1) return
    if (standardCode) return
    setLoading(true)
    void fetchWorkflowList()
      .then((d) => setWorkflows(d.workflows || []))
      .catch(() => setWorkflows([]))
      .finally(() => setLoading(false))
  }, [standardCode])

  const visibleWorkflows = React.useMemo(() => {
    if (!effectiveAccess) return []
    const visibleCodes = new Set(
      resolveVisibleStandardCodes([...enabledStandards], effectiveAccess),
    )
    return workflows.filter((w) =>
      visibleCodes.has(normalizeComplianceStandardCode(w.standardCode)),
    )
  }, [effectiveAccess, enabledStandards, workflows])

  if (!featureFlags.COMPLIANCE_WORKFLOW_V1) {
    return <Navigate to="/compliance-center/overview" replace />
  }

  if (standardCode) {
    return <ComplianceWorkflowWorkspace standardCode={decodeURIComponent(standardCode)} />
  }

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={t('complianceCenterUi.nav.operationalWorkflow', 'Operational Workflow')}
        description={t(
          'complianceWorkflow.selectStandardWithReadiness',
          'Select a granted standard for scope, applicability, certification, and per-standard readiness. Organization-level audit readiness summary is below.',
        )}
      >
        {loading || accessLoading ? <p className={wfStyles.loadingText}>…</p> : null}
        {!loading && !accessLoading && visibleWorkflows.length === 0 ? (
          <p className={wfStyles.loadingText}>
            {t('standardGovernance.noStandardsGranted', 'No standards granted.')}
          </p>
        ) : null}
        {visibleWorkflows.length > 0 ? (
          <div className={wfStyles.standardGrid}>
            {visibleWorkflows.map((w) => (
              <article key={w.standardCode} className={wfStyles.standardCard}>
                <h3 className={wfStyles.standardCardTitle}>
                  {getStandardDisplayName(t, w.standardCode)}
                </h3>
                <p className={wfStyles.standardCardMeta}>{w.workflowStage}</p>
                <Button
                  type="button"
                  size="small"
                  color="brand"
                  onClick={() => navigate(`/compliance-workflow/${encodeURIComponent(w.standardCode)}`)}
                >
                  {isWorkflowActiveStage(w.workflowStage)
                    ? t('complianceWorkflow.openWorkflow', 'Open workflow')
                    : t('complianceWorkflow.startWorkflow', 'Start workflow')}
                </Button>
              </article>
            ))}
          </div>
        ) : null}
      </GovernanceListingSection>

      <OperationalWorkflowAuditReadinessSummary />
    </div>
  )
}
